import { LoadingButton } from '@mui/lab';
import { Alert, Grid, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { AccessGroupClientModel, useListAccessGroupUnits } from '@client-app-sdk';

import AlertStatusMessage from '@common/components/AlertStatusMessage';
import UnitsFilter from '@common/components/UnitsFilter';
import { AccessUnitStatusClient } from '@common/constants';
import { ErrorAlert } from '@common/ui/ErrorAlert';
import { Loader } from '@common/ui/Loader';
import ResourceDetailedItem from '@common/ui/ResourceDetailedItem';

export default function AccessGroupUnits({ group }: { group: AccessGroupClientModel }) {
  const [rtFilters, setRtFilters] = useState<string[]>([]);

  return (
    <Stack direction="column" spacing={2}>
      <UnitsFilter
        title="Filter by"
        options={group.resource_types.map((rt) => ({
          label: rt.display_path,
          value: rt.id,
          icon: rt.icons.svg,
        }))}
        value={rtFilters}
        onChange={setRtFilters}
      />
      <AccessGroupUnitsList group={group} resourceTypeIds={rtFilters} />
    </Stack>
  );
}

function AccessGroupUnitsList({
  group,
  resourceTypeIds,
}: {
  group: AccessGroupClientModel;
  resourceTypeIds: string[];
}) {
  const { data, isFetched, isFetching, error, fetchNextPage, hasNextPage } = useListAccessGroupUnits({
    id: group.id,
    resourceTypeIds,
  });

  const accessUnits = useMemo(() => data?.pages.map((d) => d.data).flat(), [data]);

  if (!isFetched) {
    return <Loader size={30} />;
  }

  if (error) {
    return <ErrorAlert error={error} />;
  }

  if (!accessUnits) {
    return <Alert severity="error">Access group units not found</Alert>;
  }

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="helperText" color="text.disabled">
            Resources
          </Typography>
          <Typography variant="helperText" color="text.disabled">
            ({accessUnits.length} total)
          </Typography>
        </Stack>
        <Typography variant="helperText" color="text.disabled">
          Permission
        </Typography>
      </Stack>
      {accessUnits.map(({ resource, permission, status }) => (
        <AlertStatusMessage
          key={resource.id}
          message={status?.status === AccessUnitStatusClient.Failure ? 'Failed' : undefined}
        >
          <Grid container alignItems="center">
            <Grid item container justifyContent="flex-start" xs={6}>
              <ResourceDetailedItem
                icon={resource.type.icons.svg}
                displayName={resource.path}
                logicalPath={resource.type.display_path}
              />
            </Grid>
            <Grid item container justifyContent="flex-end" xs={6}>
              <Typography variant="inputLabel" color="text.muted" noWrap>
                {permission.name}
              </Typography>
            </Grid>
          </Grid>
        </AlertStatusMessage>
      ))}
      {hasNextPage && (
        <Stack alignItems="center">
          <LoadingButton loading={isFetching} size="small" variant="text" onClick={() => fetchNextPage()}>
            Load more
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
}
